import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Check, X, Pencil, Trash2, Thermometer, GraduationCap, UserPlus, BarChart3 } from 'lucide-react';

export default function Urlaubsverwaltung() {
  const { API, user, isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [absenceStats, setAbsenceStats] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showSick, setShowSick] = useState(false);
  const [showAdminAbsence, setShowAdminAbsence] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [form, setForm] = useState({ start_date: '', end_date: '', reason: '', type: 'urlaub' });
  const [sickForm, setSickForm] = useState({ start_date: '', end_date: '', reason: '' });
  const [adminForm, setAdminForm] = useState({ user_id: '', start_date: '', end_date: '', type: 'krank', reason: '' });
  const [editForm, setEditForm] = useState({ start_date: '', end_date: '', reason: '', type: '' });

  const canManage = user?.role === 'admin' || user?.role === 'projektleiter';

  useEffect(() => {
    Promise.all([axios.get(`${API}/vacation-requests`), axios.get(`${API}/users`)])
      .then(([r, u]) => { setRequests(r.data); setUsers(u.data); });
    if (isAdmin || user?.role === 'projektleiter') {
      axios.get(`${API}/absence-stats`).then(r => setAbsenceStats(r.data)).catch(() => {});
    }
  }, [API, isAdmin, user]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/vacation-requests`, form);
      setRequests(prev => [...prev, res.data]);
      setShowCreate(false);
      setForm({ start_date: '', end_date: '', reason: '', type: 'urlaub' });
      toast.success('Antrag eingereicht');
    } catch { toast.error('Fehler'); }
  };

  const handleSickReport = async () => {
    try {
      const res = await axios.post(`${API}/sick-report`, { ...sickForm, type: 'krank' });
      setRequests(prev => [...prev, res.data]);
      setShowSick(false);
      setSickForm({ start_date: '', end_date: '', reason: '' });
      toast.success('Krankmeldung erfasst');
    } catch { toast.error('Fehler'); }
  };

  const handleAdminAbsence = async () => {
    try {
      const res = await axios.post(`${API}/admin-absence`, adminForm);
      setRequests(prev => [...prev, res.data]);
      setShowAdminAbsence(false);
      setAdminForm({ user_id: '', start_date: '', end_date: '', type: 'krank', reason: '' });
      toast.success('Abwesenheit eingetragen');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDecision = async (id, status) => {
    try {
      const res = await axios.put(`${API}/vacation-requests/${id}`, { status });
      setRequests(prev => prev.map(r => r.id === id ? res.data : r));
      toast.success(status === 'genehmigt' ? 'Genehmigt' : 'Abgelehnt');
    } catch { toast.error('Fehler'); }
  };

  const openEdit = (r) => {
    setEditingReq(r);
    setEditForm({ start_date: r.start_date, end_date: r.end_date, reason: r.reason || '', type: r.type });
    setShowEdit(true);
  };

  const handleEdit = async () => {
    try {
      const res = await axios.put(`${API}/vacation-requests/${editingReq.id}`, editForm);
      setRequests(prev => prev.map(r => r.id === editingReq.id ? res.data : r));
      setShowEdit(false);
      toast.success('Aktualisiert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/vacation-requests/${id}`);
    setRequests(prev => prev.filter(r => r.id !== id));
    toast.success('Gelöscht');
  };

  const statusColors = { ausstehend: 'text-amber-400 bg-amber-400/10 border-amber-400/20', genehmigt: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', abgelehnt: 'text-rose-400 bg-rose-400/10 border-rose-400/20' };
  const statusLabels = { ausstehend: 'Ausstehend', genehmigt: 'Genehmigt', abgelehnt: 'Abgelehnt' };
  const typeLabels = { urlaub: 'Urlaub', krank: 'Krank', schulung: 'Schulung', weiterbildung: 'Weiterbildung', ueberstunden: 'Überstunden', sonstiges: 'Sonstiges' };
  const typeColors = { urlaub: 'bg-blue-400/10 text-blue-400 border-blue-400/20', krank: 'bg-rose-400/10 text-rose-400 border-rose-400/20', schulung: 'bg-violet-400/10 text-violet-400 border-violet-400/20', weiterbildung: 'bg-violet-400/10 text-violet-400 border-violet-400/20', ueberstunden: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20', sonstiges: 'bg-slate-400/10 text-slate-400 border-slate-400/20' };

  const canEditOrDelete = (r) => canManage || (r.user_id === user?.id && r.status === 'ausstehend');

  return (
    <div className="space-y-4 animate-fade-in" data-testid="urlaub-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-3xl font-bold text-white tracking-tight">Abwesenheiten</h1>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowSick(true)} data-testid="sick-report-button"
            className="bg-rose-500/10 text-rose-400 border border-rose-400/20 hover:bg-rose-500/20 rounded-sm">
            <Thermometer className="w-4 h-4 mr-2" />Krankmeldung
          </Button>
          {canManage && (
            <Button onClick={() => setShowAdminAbsence(true)} data-testid="admin-absence-button"
              className="bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 rounded-sm">
              <UserPlus className="w-4 h-4 mr-2" />Abwesenheit eintragen
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} data-testid="create-vacation-button"
            className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <Plus className="w-4 h-4 mr-2" />Urlaub beantragen
          </Button>
        </div>
      </div>

      {/* Admin: Abwesenheits-Statistik */}
      {(isAdmin || user?.role === 'projektleiter') && absenceStats.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800/40 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-bold text-white uppercase tracking-wider">Jahresübersicht {new Date().getFullYear()}</span>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-900/80 border-b border-slate-800/40">
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500">Mitarbeiter</th>
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500 text-center">Urlaubstage gesamt</th>
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500 text-center">Genommen</th>
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500 text-center">Resturlaub</th>
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500 text-center">Krankentage</th>
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500 text-center">Schulung</th>
                <th className="px-4 py-2 text-xs font-mono uppercase text-slate-500 text-center">Überstunden</th>
              </tr>
            </thead>
            <tbody>
              {absenceStats.map(s => (
                <tr key={s.user_id} className="border-b border-slate-800/30 hover:bg-slate-900/80">
                  <td className="px-4 py-2.5 text-sm text-slate-200">{s.user_name}</td>
                  <td className="px-4 py-2.5 text-sm font-mono text-slate-400 text-center">{s.vacation_total}</td>
                  <td className="px-4 py-2.5 text-sm font-mono text-blue-400 text-center">{s.vacation_used}</td>
                  <td className="px-4 py-2.5 text-sm font-mono text-center">
                    <span className={s.vacation_remaining <= 5 ? 'text-amber-400 font-bold' : 'text-emerald-400'}>{s.vacation_remaining}</span>
                  </td>
                  <td className="px-4 py-2.5 text-sm font-mono text-center">
                    <span className={s.sick_days > 0 ? 'text-rose-400' : 'text-slate-600'}>{s.sick_days}</span>
                  </td>
                  <td className="px-4 py-2.5 text-sm font-mono text-violet-400 text-center">{s.training_days || 0}</td>
                  <td className="px-4 py-2.5 text-sm font-mono text-cyan-400 text-center">{s.overtime_days || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_100px_100px_110px_150px] gap-4 px-4 py-3 bg-slate-900/80 text-xs font-mono uppercase text-slate-500 tracking-wider border-b border-slate-800/40">
          <span>Mitarbeiter</span><span>Zeitraum</span><span>Typ</span><span>Status</span><span>Bearbeitet</span><span>Aktionen</span>
        </div>
        {requests.length ? requests.map(r => (
          <div key={r.id} className="grid grid-cols-[1fr_1fr_100px_100px_110px_150px] gap-4 px-4 py-3 border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors items-center"
            data-testid={`vacation-row-${r.id}`}>
            <div>
              <span className="text-sm text-slate-300">{r.user_name}</span>
              {r.reason && <p className="text-xs text-slate-500 truncate mt-0.5">{r.reason}</p>}
            </div>
            <span className="text-sm font-mono text-slate-400">{r.start_date} — {r.end_date}</span>
            <Badge className={`${typeColors[r.type] || typeColors.sonstiges} rounded-sm text-xs`}>{typeLabels[r.type] || r.type}</Badge>
            <Badge className={`${statusColors[r.status] || ''} rounded-sm text-xs`}>{statusLabels[r.status] || r.status}</Badge>
            <span className="text-xs text-slate-500 truncate">{r.reviewed_by || '—'}</span>
            <div className="flex gap-1">
              {canManage && r.status === 'ausstehend' && (
                <>
                  <Button variant="ghost" size="icon" onClick={() => handleDecision(r.id, 'genehmigt')}
                    className="text-emerald-400 hover:bg-emerald-400/10 w-8 h-8" data-testid={`approve-${r.id}`}><Check className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDecision(r.id, 'abgelehnt')}
                    className="text-rose-400 hover:bg-rose-400/10 w-8 h-8" data-testid={`reject-${r.id}`}><X className="w-4 h-4" /></Button>
                </>
              )}
              {canEditOrDelete(r) && (
                <>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="text-slate-400 hover:text-amber-400 w-8 h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} className="text-slate-400 hover:text-rose-400 w-8 h-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </>
              )}
            </div>
          </div>
        )) : <div className="p-8 text-center text-slate-500">Keine Einträge vorhanden</div>}
      </div>

      {/* Urlaub beantragen */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Urlaub beantragen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Von</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-300">Bis</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
            </div>
            <div><Label className="text-slate-300">Grund (optional)</Label>
            <Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Grund..." className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
            <Button onClick={handleCreate} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">Antrag einreichen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Krankmeldung (Selbstmeldung) */}
      <Dialog open={showSick} onOpenChange={setShowSick}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Thermometer className="w-5 h-5 text-rose-400" />Krankmeldung</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-3 text-sm text-rose-300">
              Ihre Krankmeldung wird sofort erfasst. Bitte reichen Sie ggf. ein ärztliches Attest nach.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Von</Label><Input data-testid="sick-start" type="date" value={sickForm.start_date} onChange={e => setSickForm({...sickForm, start_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-300">Voraussichtlich bis</Label><Input data-testid="sick-end" type="date" value={sickForm.end_date} onChange={e => setSickForm({...sickForm, end_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
            </div>
            <div><Label className="text-slate-300">Anmerkung (optional)</Label>
            <Textarea value={sickForm.reason} onChange={e => setSickForm({...sickForm, reason: e.target.value})} placeholder="z.B. Arzttermin am..." className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
            <Button onClick={handleSickReport} data-testid="submit-sick" className="w-full bg-rose-500 text-white hover:bg-rose-400 font-bold rounded-sm">
              Krankmeldung abschicken
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: Abwesenheit eintragen */}
      <Dialog open={showAdminAbsence} onOpenChange={setShowAdminAbsence}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Abwesenheit eintragen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Mitarbeiter</Label>
              <Select value={adminForm.user_id} onValueChange={v => setAdminForm({...adminForm, user_id: v})}>
                <SelectTrigger data-testid="absence-user" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {users.map(u => <SelectItem key={u.id} value={u.id} className="text-slate-200">{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Art der Abwesenheit</Label>
              <Select value={adminForm.type} onValueChange={v => setAdminForm({...adminForm, type: v})}>
                <SelectTrigger data-testid="absence-type" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="urlaub" className="text-slate-200">Urlaub</SelectItem>
                  <SelectItem value="krank" className="text-slate-200">Krank</SelectItem>
                  <SelectItem value="schulung" className="text-slate-200">Schulung</SelectItem>
                  <SelectItem value="weiterbildung" className="text-slate-200">Weiterbildung</SelectItem>
                  <SelectItem value="ueberstunden" className="text-slate-200">Überstunden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Von</Label><Input data-testid="absence-start" type="date" value={adminForm.start_date} onChange={e => setAdminForm({...adminForm, start_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-300">Bis</Label><Input data-testid="absence-end" type="date" value={adminForm.end_date} onChange={e => setAdminForm({...adminForm, end_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
            </div>
            <div><Label className="text-slate-300">Grund</Label>
            <Textarea value={adminForm.reason} onChange={e => setAdminForm({...adminForm, reason: e.target.value})} placeholder="z.B. Erste-Hilfe-Kurs, Maschinenschein..."
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
            <Button onClick={handleAdminAbsence} data-testid="submit-admin-absence" className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              Abwesenheit eintragen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bearbeiten */}
      <Dialog open={showEdit} onOpenChange={v => { setShowEdit(v); if (!v) setEditingReq(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Eintrag bearbeiten</DialogTitle></DialogHeader>
          {editingReq && (
            <div className="space-y-4">
              <div className="bg-slate-950/50 rounded-sm p-3 border border-slate-800/40 flex items-center gap-3">
                <span className="text-sm text-slate-300">{editingReq.user_name}</span>
                <Badge className={`${typeColors[editingReq.type] || ''} rounded-sm text-xs`}>{typeLabels[editingReq.type]}</Badge>
                <Badge className={`${statusColors[editingReq.status] || ''} rounded-sm text-xs`}>{statusLabels[editingReq.status]}</Badge>
              </div>
              <div>
                <Label className="text-slate-300">Typ</Label>
                <Select value={editForm.type} onValueChange={v => setEditForm({...editForm, type: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="urlaub" className="text-slate-200">Urlaub</SelectItem>
                    <SelectItem value="krank" className="text-slate-200">Krank</SelectItem>
                    <SelectItem value="schulung" className="text-slate-200">Schulung</SelectItem>
                    <SelectItem value="weiterbildung" className="text-slate-200">Weiterbildung</SelectItem>
                    <SelectItem value="ueberstunden" className="text-slate-200">Überstunden</SelectItem>
                    <SelectItem value="sonstiges" className="text-slate-200">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Von</Label><Input type="date" value={editForm.start_date} onChange={e => setEditForm({...editForm, start_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
                <div><Label className="text-slate-300">Bis</Label><Input type="date" value={editForm.end_date} onChange={e => setEditForm({...editForm, end_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              </div>
              <div><Label className="text-slate-300">Grund</Label>
              <Textarea value={editForm.reason} onChange={e => setEditForm({...editForm, reason: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              <Button onClick={handleEdit} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">Speichern</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
